import { UseChatHelpers } from '@ai-sdk/react';
import type { LlmUIMessage } from '@azymmetric/shared/types/llm.types';
import { Conversation, ConversationContent, ConversationScrollButton } from '@components/ai-elements/conversation';
import { Message, MessageContent } from '@components/ai-elements/message';
import { Response } from '@components/ai-elements/response';
import { Spinner } from '@components/shared/spinner.component';
import { FileTextIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';

type LlmChatMessagesProps = {
  emptyState?: ReactNode;
  messages: LlmUIMessage[];
  status: UseChatHelpers<LlmUIMessage>['status'];
};

const SuggestionToolChip: FC<{ isInvalid?: boolean; isStreaming: boolean }> = (props) => {
  const { isInvalid = false, isStreaming } = props;

  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
      {isStreaming ? (
        <>
          <Spinner size="sm" />
          <span>Drafting suggestion…</span>
        </>
      ) : (
        <>
          <FileTextIcon className="size-4 text-gray-500" />
          <span>
            {isInvalid
              ? 'Draft failed validation — details in the panel.'
              : 'Suggestion updated — review it in the panel.'}
          </span>
        </>
      )}
    </div>
  );
};

const isSuggestionToolPart = (part: LlmUIMessage['parts'][number]): boolean =>
  part.type === 'tool-proposeSuggestion' || part.type === 'tool-proposeData';

export const LlmChatMessages: FC<LlmChatMessagesProps> = (props) => {
  const { emptyState, messages, status } = props;

  const lastMessage = messages.at(-1);
  const hasAssistantContent = lastMessage?.parts.some((part) => {
    if (part.type === 'text') {
      return Boolean(part.text);
    }

    return part.type.startsWith('tool-');
  });
  const showLoader =
    status === 'submitted' || (status === 'streaming' && lastMessage?.role === 'assistant' && !hasAssistantContent);

  return (
    <Conversation className="min-h-0 w-full flex-1">
      <ConversationContent className="p-2">
        {messages.length === 0 && emptyState}

        {messages.map((message) => {
          // The model may call the suggestion tool multiple times in one
          // exchange (e.g. self-correcting after a validation failure); render
          // a single chip for the latest call instead of one per attempt.
          const lastSuggestionPartIndex = message.parts.reduce(
            (last, part, index) => (isSuggestionToolPart(part) ? index : last),
            -1,
          );

          return (
            <Message
              from={message.role}
              key={message.id}
            >
              {message.parts.map((part, partIndex) => {
                switch (part.type) {
                  case 'text':
                    return (
                      <MessageContent key={`${message.id}-${partIndex}`}>
                        <Response>{part.text}</Response>
                      </MessageContent>
                    );
                  case 'tool-proposeSuggestion':
                    if (partIndex !== lastSuggestionPartIndex) return null;

                    return (
                      <SuggestionToolChip
                        isStreaming={part.state === 'input-streaming'}
                        key={`${message.id}-${partIndex}`}
                      />
                    );
                  case 'tool-proposeData':
                    if (partIndex !== lastSuggestionPartIndex) return null;

                    return (
                      <SuggestionToolChip
                        isInvalid={part.state === 'output-available' && !part.output.valid}
                        // input-available covers the server-side extraction window:
                        // the draft is still validating, so the panel shows its
                        // placeholder and the chip must keep saying "drafting".
                        isStreaming={part.state === 'input-streaming' || part.state === 'input-available'}
                        key={`${message.id}-${partIndex}`}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </Message>
          );
        })}

        {showLoader && (
          <Message from="assistant">
            <MessageContent>
              <div className="flex items-center gap-2 py-2">
                <Spinner size="sm" />
              </div>
            </MessageContent>
          </Message>
        )}
      </ConversationContent>

      <ConversationScrollButton />
    </Conversation>
  );
};
